/**
 * The app icon badge: the number of agent conversations that need the creator (the queue's `needs_you`).
 *
 * Set from the app while it runs (`@capawesome/capacitor-badge`, guarded: older binaries don't have it). It never
 * prompts: on iOS the badge needs notification permission, which the push prompt asks for. While the app is in
 * the background the badge keeps the last value; the backend can keep it fresh with `aps.badge` on pushes.
 */
import { Capacitor } from '@capacitor/core'
import { Badge } from '@capawesome/capacitor-badge'
import { isNativePlatform } from '@/utils/platform'

/** A "not allowed" answer is re-checked after this long (the creator may allow notifications meanwhile). */
const DENIED_RECHECK_MS = 60_000

let last: number | null = null
let allowed: boolean | null = null
let checkedAt = 0

function available(): boolean {
    return isNativePlatform() && Capacitor.isPluginAvailable('Badge')
}

async function permitted(): Promise<boolean> {
    if (allowed === true || (allowed === false && Date.now() - checkedAt < DENIED_RECHECK_MS)) return allowed
    checkedAt = Date.now()
    try {
        const { isSupported } = await Badge.isSupported()
        if (!isSupported) return (allowed = false)
        const { display } = await Badge.checkPermissions()
        // 'prompt' is only asked by the push prompt; until then a set would be dropped (iOS) or is fine (Android).
        allowed = display === 'granted'
        return allowed
    } catch {
        allowed = false
        return false
    }
}

/** Show `count` on the app icon (0 clears it). Repeated values are skipped. */
export async function setAppBadge(count: number): Promise<void> {
    if (!available()) return
    const n = Math.max(0, Math.floor(count || 0))
    if (n === last) return
    if (!(await permitted())) return
    last = n
    try {
        if (n === 0) await Badge.clear()
        else await Badge.set({ count: n })
    } catch {
        last = null
    }
}

/** Permission may have changed (the push prompt was answered): check again on the next set. */
export function recheckBadgePermission(): void {
    allowed = null
    last = null
}

/** Signed out: no badge for an account that isn't here. */
export async function clearAppBadge(): Promise<void> {
    if (!available()) return
    last = 0
    try {
        await Badge.clear()
    } catch {
        // ignore
    }
}
